/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Component, ComponentRef, EventEmitter, Input, OnDestroy, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { IMasonryCardBase } from '@app/app/shared/directives/masonry.directive';
import { LucideBookmark, LucideFileText, LucideHeart, LucideMessageSquare, LucidePaperclip } from '@lucide/angular';
import { Select, Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { AppFrameState } from '../../../../../../../app-frame/store/app-frame.state';
import { ProfileImageSummary } from '../../../../../../../shared/components/profile-image/profile-image.component';
import { V3MomentCardCommentsComponent } from '../../../../../../../shared/components/v3-moment-card-comments/v3-moment-card-comments.component';
import { EditorContent } from '../../../../../../../shared/models/editor/editor-content.model';
import { MediumEditorData } from '../../../../../../../shared/models/editor/media-content.model';
import { UserDetails } from '../../../../../../../user-auth/models';
import { UserAuthState } from '../../../../../../../user-auth/store/user-auth.state';
import { MomentCard } from '../../../../../models';
import { DisableFolioSectionItemVisibility, EnableFolioSectionItemVisibility } from '../../../../../store/folio.action';
import { FolioSectionItemRequest, FolioSectionItemType } from '../../../../../store/folio.state.model';
import { DeleteMoment } from '../../../../../store/moment/moment-view-all/moment-view-all.actions';

@Component({
  selector: 'ptl-v3-folio-masonry-card',
  templateUrl: './v3-folio-masonry-card.component.html',
  styleUrls: ['./v3-folio-masonry-card.component.scss'],
})
export class V3FolioMasonryCardComponent implements IMasonryCardBase, OnDestroy {
  private _card: MomentCard;

  /** Card data. */
  @Input()
  get card(): MomentCard {
    return this._card;
  }

  set card(value: MomentCard) {
    this._card = value;
    this.previewContent = this.mergeConsecutiveParagraphs(value?.content);
  }

  /** define if moment should be archived. */
  @Input() archiveMoment: boolean;
  @Input() selected;
  @Input() uid: string;
  @Input() editMode: boolean;
  @Input() isFolioSection: boolean;
  @Input() cardVariant: 'default' | 'community' = 'default';

  @Output() cardClick = new EventEmitter<MomentCard>();
  @Output() removeCard = new EventEmitter<string>();
  @Output() likeMoment = new EventEmitter<string>();
  @Output() unlikeMoment = new EventEmitter<string>();
  @Output() saveMoment = new EventEmitter<string>();
  @Output() unsaveMoment = new EventEmitter<string>();

  @ViewChild('commentsContainer', { read: ViewContainerRef }) commentsContainerRef: ViewContainerRef;

  @Select(UserAuthState.userDetailsData)
  userDetailsData$: Observable<UserDetails>;

  @Select(AppFrameState.isMobile)
  isMobile$: Observable<boolean>;

  protected readonly LucideMessageSquare = LucideMessageSquare;
  protected readonly LucideHeart = LucideHeart;
  protected readonly LucideBookmark = LucideBookmark;
  protected readonly LucideFileText = LucideFileText;

  commentsOpen = false;
  previewContent: EditorContent[] = [];
  private commentsRef: ComponentRef<V3MomentCardCommentsComponent> | null = null;
  private commentCountSub?: Subscription;

  constructor(private store: Store) {}

  get authorProfile(): ProfileImageSummary | undefined {
    return this.card?.author ? { firstName: this.card.author.fullName, imageUrl: this.card.author.avatarUrl } : undefined;
  }

  ngOnDestroy(): void {
    this.commentCountSub?.unsubscribe();
    this.commentsRef?.destroy();
    this.commentsRef = null;
  }

  /**
   * The moment editor can store one logical block of text as several consecutive
   * PARAGRAPH content items. Each item renders its own ptl-resource-html-preview,
   * which would otherwise show its own "See more" button, so merge them into one
   * paragraph before truncation is applied.
   */
  private mergeConsecutiveParagraphs(content: EditorContent[] | undefined): EditorContent[] {
    if (!content?.length) {
      return content ?? [];
    }

    const merged: EditorContent[] = [];
    for (const item of content) {
      const previous = merged[merged.length - 1];
      if (item.type === 'PARAGRAPH' && previous?.type === 'PARAGRAPH') {
        (previous as MediumEditorData).content =
          `${(previous as MediumEditorData).content || ''}${(item as MediumEditorData).content || ''}`;
      } else {
        merged.push({ ...item });
      }
    }
    return merged;
  }

  onRemoveMoment(id: string) {
    this.removeCard.emit(id);
  }

  onArchiveMoment(id: string) {
    this.store.dispatch(new DeleteMoment(id));
  }

  onItemVisibilityState(card: MomentCard, event: Event) {
    event.stopPropagation();

    const request: FolioSectionItemRequest = {
      type: FolioSectionItemType.MOMENTS,
      uid: card._id,
    };

    if (card.visible) {
      this.store.dispatch(new DisableFolioSectionItemVisibility(this.uid, request));
    } else {
      this.store.dispatch(new EnableFolioSectionItemVisibility(this.uid, request));
    }

    card.visible = !card.visible;
  }

  onLikeMoment(event: Event) {
    event.stopPropagation();

    if (this.card.likes?.liked) {
      this.unlikeMoment.emit(this.card._id);
    } else {
      this.likeMoment.emit(this.card._id);
    }
  }

  onSaveMoment(event: Event) {
    event.stopPropagation();

    if (this.card.saved) {
      this.unsaveMoment.emit(this.card._id);
    } else {
      this.saveMoment.emit(this.card._id);
    }
  }

  toggleComments(event: Event): void {
    event.stopPropagation();

    if (this.commentsRef) {
      this.commentCountSub?.unsubscribe();
      this.commentsRef.destroy();
      this.commentsRef = null;
      this.commentsOpen = false;
      return;
    }

    this.commentsRef = this.commentsContainerRef.createComponent(V3MomentCardCommentsComponent);
    this.commentsRef.setInput('momentId', this.card._id);
    this.commentsRef.setInput('initialCommentsCount', this.card.commentsCount || 0);
    this.commentCountSub = this.commentsRef.instance.commentCountUpdate.subscribe((count: number) => {
      this.card.commentsCount = count;
    });
    this.commentsOpen = true;
  }

  onButtonClick() {
    this.cardClick.emit(this.card);
  }

  protected readonly LucidePaperclip = LucidePaperclip;
}
